import { forwardRef } from 'react';
import { format } from 'date-fns/fp';
import { ChevronRight } from 'lucide-react';

import type { Alert } from '@/entities/alert';
import {
  alertTypeMapping,
  alertTypeToFeatureFlag,
} from '@/entities/alert-type';
import { cls } from '@/utils/cls';

import { StatusIcon } from './alert-status-badge';
import { AlertsSidebarInternalIcon } from './alerts-sidebar-internal-icon';
import { TaskPreview } from './task-preview';

export type AlertsSidebarItemProps = {
  selected?: boolean;
  alert: Alert;
  onClick?: () => void;
};

const formatDateLabel = format('yyyy-MM-dd');

// SidebarItem Component
// This component displays a sidebar item with alert information and handles item selection with a visual indicator.
export const AlertsSidebarItem = forwardRef<
  HTMLDivElement,
  AlertsSidebarItemProps
>(({ alert, selected = false, onClick }, ref) => {
  // Get the feature flag for this alert type from the centralized mapping
  const featureFlag = alertTypeToFeatureFlag[alert.type];

  return (
    <div
      data-testid="alert-list-item"
      className={cls(
        'flex cursor-pointer flex-row border-b border-gray-200 bg-white p-2 transition-all hover:bg-gray-50',
        {
          'border-l-4 border-primary-500 bg-primary-50 font-semibold text-primary-700':
            selected,
        },
      )}
      onClick={onClick}
      ref={ref}
    >
      {/* Left Content Section: Main alert details */}
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start self-stretch overflow-hidden pl-6">
        {/* Alert Status and ID Section */}
        <div className="mr-2 inline-flex shrink grow basis-0 items-center justify-center self-stretch overflow-hidden">
          {/* Alert Status Badge */}
          <StatusIcon status={alert.status} />
          <div className="relative w-[7px]" />

          {/* Alert ID */}
          <div className="inline-flex shrink grow basis-0 flex-col items-center justify-center self-stretch overflow-hidden">
            <div
              className="self-stretch truncate text-sm leading-tight"
              data-testid="alert-list-item-id"
            >
              {alert.fullFrontendId}
            </div>
          </div>

          <TaskPreview
            task={alert.task}
            className={cls(
              'text-[#637381] hover:bg-neutral-200 hover:text-[#637381]',
              {
                'text-primary-700': selected,
              },
            )}
          />
        </div>

        {/* Alert Status and Type Badges Section */}
        <div className="inline-flex shrink grow basis-0 items-center justify-start self-stretch overflow-hidden">
          <div className="flex items-start justify-start gap-2.5">
            {/* Alert Status Badge */}
            <div className="flex items-center justify-center gap-1 rounded-full border border-[#637381] px-2 py-1">
              <div className="text-xs font-normal leading-none text-[#637381]">
                {alert.status}
              </div>
            </div>
          </div>

          <div className="relative h-[34px] w-[2px]" />

          {/* Alert Type Badge */}
          <div className="flex items-center justify-center">
            <div className="flex items-center justify-center gap-1 rounded-full border border-[#637381] px-2 py-1">
              <div className="text-xs font-normal leading-none text-[#637381]">
                {alertTypeMapping[alert.type]}
              </div>
            </div>
          </div>
        </div>

        {/* Suggested Action and Date Section */}
        <div className="inline-flex w-full items-center justify-between overflow-hidden">
          {/* Suggested Action */}
          <div className="min-w-0 flex-1">
            <div
              className="truncate whitespace-nowrap text-xs font-normal leading-none text-neutral-500"
              title={alert.suggestedAction} // Tooltip with full text on hover
            >
              {alert.suggestedAction}
            </div>
          </div>

          {/* Date Changed */}
          <div className="inline-flex w-[100px] shrink-0 flex-col items-center justify-center">
            <div className="flex h-4 flex-col items-center justify-center self-stretch">
              <div className="text-center text-xs font-normal leading-none text-neutral-500">
                {formatDateLabel(alert.changedAt)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative inline-flex h-[94px] w-[18px] flex-col items-center justify-center px-1">
        <ChevronRight
          className={cls('size-5 flex-none text-gray-400 transition-all', {
            'text-primary-700': selected,
          })}
          aria-hidden="true"
        />
        {featureFlag && <AlertsSidebarInternalIcon featureFlag={featureFlag} />}
      </div>
    </div>
  );
});

// Add a display name for debugging
AlertsSidebarItem.displayName = 'AlertsSidebarItem';
